import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProductCategories, getProducts } from '../data/products';
import { useI18n } from '../i18n';
import { navigateWithFeedback } from '../utils/navigationFeedback';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useI18n();
  const products = getProducts(language);
  const productCategories = getProductCategories(language);
  const featuredProducts = products.slice(0, 4);
  const advantages = t('home.advantages');
  const reasons = t('home.reasons');
  const documents = [
    { id: 'declaration', name: t('home.documents.declaration'), path: '/assets/decl.png' },
    { id: 'certificate', name: t('home.documents.certificate'), path: '/assets/sert.png' },
  ];
  const getProductsByCategory = (category) =>
    category.productTargets
      .map((target) => products.find((product) => product.target === target))
      .filter(Boolean);

  const navigateTo = (target = '/', section) => {
    navigateWithFeedback({
      currentPath: location.pathname,
      navigate,
      section,
      target,
      headerOffset: 76,
      message: t('nav.alreadyHere'),
    });
  };

  const openDocument = (doc) => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;

    newWindow.document.write(`
      <html>
        <head>
          <title>${doc.name}</title>
          <style>
            html,
            body {
              margin: 0;
              width: 100%;
              height: 100%;
              overflow: hidden;
              background: #111315;
            }
            body {
              padding: 0;
              display: grid;
              place-items: center;
              font-family: Arial, sans-serif;
            }
            .viewer {
              width: 100vw;
              height: 100vh;
              display: grid;
              place-items: center;
              padding: 28px 22px;
            }
            img {
              width: auto;
              height: calc(100vh - 56px);
              max-width: calc(100vw - 44px);
              max-height: calc(100vh - 56px);
              background: #fff;
              border-radius: 8px;
              object-fit: contain;
              box-shadow: 0 24px 70px rgba(0, 0, 0, 0.35);
            }
            .controls {
              position: fixed;
              top: 20px;
              right: 20px;
              display: flex;
              gap: 10px;
            }
            button {
              border: 0;
              border-radius: 999px;
              padding: 10px 16px;
              color: #fff;
              background: #d51f2a;
              cursor: pointer;
              font-weight: 700;
            }
            @media print {
              html,
              body {
                height: auto;
                overflow: visible;
                background: #fff;
              }
              .viewer {
                width: auto;
                height: auto;
                padding: 0;
              }
              .controls {
                display: none;
              }
              img {
                max-width: 100%;
                max-height: none;
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="controls">
            <button onclick="document.documentElement.requestFullscreen?.()">${t('buttons.fullscreen')}</button>
            <button onclick="window.print()">${t('buttons.print')}</button>
            <button onclick="window.close()">${t('buttons.close')}</button>
          </div>
          <main class="viewer">
            <img src="${doc.path}" alt="${doc.name}" />
          </main>
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  const downloadDocument = (doc, event) => {
    event.stopPropagation();
    const link = document.createElement('a');
    link.href = doc.path;
    link.download = `${doc.name.toLowerCase().replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <main className="home-page">
      <section
        className="home-hero"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(12, 14, 14, 0.92) 0%, rgba(12, 14, 14, 0.78) 38%, rgba(12, 14, 14, 0.22) 76%), url('/assets/medeo-hero-v2.png')",
        }}
      >
        <div className="home-hero__content">
          <div className="hero-brand">
            <img className="hero-logo" src="/assets/logo-clean.png" alt={t('brand.companyName')} />
            <span className="eyebrow">{t('home.heroEyebrow')}</span>
          </div>
          <h1 className="sr-only">{t('brand.companyName')}</h1>
          <p>{t('home.heroText')}</p>
          <div className="hero-actions">
            <button
              className="button button--ghost"
              onClick={() => navigateTo('/product')}
            >
              {t('buttons.viewProducts')}
            </button>
          </div>
          <div className="hero-metrics" aria-label={t('home.heroFactsLabel')}>
            <div>
              <strong>6</strong>
              <span>{t('home.metricLines')}</span>
            </div>
            <div>
              <strong>ГОСТ</strong>
              <span>{t('home.metricDocs')}</span>
            </div>
            <div>
              <strong>2</strong>
              <span>{t('home.metricCountries')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="category-strip" aria-label={t('home.categoriesLabel')}>
        {productCategories.map((category) => (
          <button
            className="category-card"
            data-reveal
            data-tilt
            key={category.target}
            onClick={() => navigateTo('/product', category.target)}
          >
            <span className="category-card__media" aria-hidden="true">
              {getProductsByCategory(category).map((product) => (
                <img src={product.image} alt="" key={product.target} loading="lazy" />
              ))}
            </span>
            <span className="category-card__content">
              <strong>{category.title}</strong>
              <small>{category.text}</small>
            </span>
          </button>
        ))}
      </section>

      <section className="section section--light" id="catalog" data-reveal>
        <div className="section__inner">
          <div className="section-heading section-heading--split">
            <div>
              <span className="eyebrow">{t('home.productsEyebrow')}</span>
              <h2>{t('home.productsTitle')}</h2>
            </div>
            <p>{t('home.productsText')}</p>
          </div>

          <div className="product-grid">
            {featuredProducts.map((product) => (
              <article
                className="product-card"
                data-reveal
                data-tilt
                key={product.target}
                onClick={() => navigateTo('/product', product.target)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigateTo('/product', product.target);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="product-card__image">
                  <img src={product.image} alt={product.name} loading="lazy" />
                </div>
                <div className="product-card__body">
                  <span>{product.weights}</span>
                  <h3>{product.name}</h3>
                  <p>{product.accent}</p>
                  <span className="link-button product-card__cta">
                    {t('buttons.details')}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="center-action">
            <button
              className="button button--dark"
              onClick={() => navigateTo('/product')}
            >
              {t('nav.allCatalog')}
            </button>
          </div>
        </div>
      </section>

      <section className="section section--ink" data-reveal>
        <div className="section__inner">
          <div className="advantage-grid">
            {advantages.map((item, index) => (
              <article className="advantage-card" data-reveal data-tilt key={`advantage-${index}`}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--split" id="company" data-reveal>
        <div className="section__inner company-layout">
          <div className="company-copy" data-reveal>
            <span className="eyebrow">{t('home.companyEyebrow')}</span>
            <h2>{t('home.companyTitle')}</h2>
            <p>{t('home.companyTextOne')}</p>
            <p>{t('home.companyTextTwo')}</p>
          </div>
          <div className="company-panel" data-reveal data-tilt>
            <img src="/assets/input.png" alt={t('home.companyImageAlt')} loading="lazy" />
            <div>
              <strong>{t('home.companyPanelTitle')}</strong>
              <span>{t('home.companyPanelText')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--light" data-reveal>
        <div className="section__inner">
          <div className="section-heading section-heading--center">
            <span className="eyebrow">{t('home.reasonsEyebrow')}</span>
            <h2>{t('home.reasonsTitle')}</h2>
          </div>
          <div className="reason-grid">
            {reasons.map((reason, index) => (
              <article className="reason-card" data-reveal data-tilt key={`reason-${index}`}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{reason.title}</h3>
                <p>{reason.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--contacts" id="documents" data-reveal>
        <div className="section__inner contact-layout">
          <div>
            <span className="eyebrow">{t('home.documentsEyebrow')}</span>
            <h2>{t('home.documentsTitle')}</h2>
            <div className="document-grid">
              {documents.map((doc) => (
                <article
                  className="document-card"
                  data-reveal
                  data-tilt
                  key={doc.id}
                  onClick={() => openDocument(doc)}
                >
                  <img src={doc.path} alt={doc.name} loading="lazy" />
                  <div>
                    <h3>{doc.name}</h3>
                    <button
                      className="link-button"
                      onClick={(event) => downloadDocument(doc, event)}
                    >
                      {t('buttons.download')}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="contact-card" id="contacts" data-reveal data-tilt>
            <span className="eyebrow">{t('home.contactsEyebrow')}</span>
            <h2>{t('home.contactsTitle')}</h2>
            <dl>
              <div>
                <dt>{t('home.addressLabel')}</dt>
                <dd>{t('home.address')}</dd>
              </div>
              <div>
                <dt>{t('home.phoneLabel')}</dt>
                <dd>
                  <a href="tel:+79043032224">+7 (904) 303-22-24</a>
                </dd>
              </div>
              <div>
                <dt>{t('home.emailLabel')}</dt>
                <dd>
                  <a href="mailto:medeo-74@mail.ru">medeo-74@mail.ru</a>
                </dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>
    </main>
  );
}
